import { useEffect, useRef, useState } from "react";
import { Profession } from "@prisma/client";
import EditIcon from "@app/icons/Edit";
import ProfileButton from "@app/components/ProfileButton";
import Input from "@app/components/Input";
import SelectCustom from "./SelectCustom";
import SelectEtablissement from "./SelectEtablissement";
import { PictureUploadResponse, UserConnexionResponse, UserMetadataResponse } from "@api/src/types/responses";
import { UserWithMyRooms } from "@api/src/types/user";
import useUser from "@app/zustand/user";

interface ProfileFormProps {
  id: string;
  defaultValues: UserWithMyRooms;
  className?: string;
  canDelete?: boolean;
  onOk: () => void;
  setLoading: (loading: boolean) => void;
}

const professionOptions = [
  { value: Profession.MEDECIN, label: "Médecin" },
  { value: Profession.INFIRMIER, label: "Infirmier.ère" },
];

export default function ProfileForm({
  id,
  defaultValues,
  className = "",
  canDelete = false,
  onOk,
  setLoading,
}: ProfileFormProps) {
  const me = useUser((state) => state.user!);
  const setUser = useUser((state) => state.setUser);
  const uploadPicRef = useRef<HTMLButtonElement>(null);

  const [userStatuses, setUserStatuses] = useState<UserMetadataResponse["data"]["userStatuses"]>([]);
  const [userSpecialites, setUserSpecialites] = useState<UserMetadataResponse["data"]["userSpecialites"]>([]);
  const [etablissements, setEtablissements] = useState<UserMetadataResponse["data"]["etablissements"]>([]);

  const medecinStatusOptions = userStatuses.map(({ status }) => ({
    value: status,
    label: status,
  }));

  const infirmierStatusOptions = ["IDE", "IADE", "IBODE", "Cadre de santé"].map((status) => ({
    value: status,
    label: status,
  }));

  const [avatarUrl, setAvatarUrl] = useState<string | null>(me?.avatar);
  const [profession, setProfession] = useState(me?.profession);

  const statusOptions = profession === Profession.INFIRMIER ? infirmierStatusOptions : medecinStatusOptions;

  useEffect(() => {
    fetch(`${import.meta.env.VITE_API_URL}/user-metadata`, {
      credentials: "include",
      method: "GET",
    })
      .then((response) => response.json() as Promise<UserMetadataResponse>)
      .then((response) => {
        if (response.error) {
          alert(response.error);
          return;
        } else if (response.ok) {
          setUserStatuses(response.data.userStatuses);
          setUserSpecialites(response.data.userSpecialites);
          setEtablissements(response.data.etablissements);
        }
      });
  }, []);

  const handleDeleteAccount = () => {
    if (
      window.confirm(
        "Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible et supprimera toutes vos données."
      )
    ) {
      fetch(`${import.meta.env.VITE_API_URL}/user/${me.plausible_id}`, {
        method: "DELETE",
        credentials: "include",
      })
        .then((response) => response.json() as Promise<UserConnexionResponse>)
        .then((response) => {
          if (response.ok) {
            window.location.href = "/welcome";
          } else if (response.error) {
            alert(response.error);
          }
        });
    }
  };

  return (
    <>
      <form
        method="post"
        encType="multipart/form-data"
        onSubmit={async (e) => {
          e.preventDefault();
          const formData = new FormData(e.currentTarget);
          const file = formData.get("picture") as File;
          if (!file || file.size === 0) return;

          formData.append("pictureName", `${me.plausible_id}-avatar`);

          await fetch(`${import.meta.env.VITE_API_URL}/picture`, {
            method: "POST",
            credentials: "include",
            body: formData,
          })
            .then((response) => response.json() as Promise<PictureUploadResponse>)
            .then((response) => {
              if (response.ok) {
                setAvatarUrl(response.data.url);
              } else if (response.error) {
                alert(response.error);
              }
            });
        }}
      >
        {avatarUrl ? (
          <label htmlFor="picture" className="relative mt-2 cursor-pointer text-sky-800">
            <ProfileButton className="relative !h-52 !w-52" user={{ ...defaultValues, avatar: avatarUrl }}>
              <EditIcon className="absolute top-2 mx-auto cursor-pointer rounded-full border-2 border-sky-900 bg-white p-1 text-sky-900 opacity-30 hover:opacity-100" />
            </ProfileButton>
          </label>
        ) : (
          <label htmlFor="picture" className="mt-2 cursor-pointer text-sky-800">
            <u>Cliquez ici</u> pour choisir votre photo de profil
          </label>
        )}

        <input
          type="file"
          onChange={() => uploadPicRef.current?.click()}
          name="picture"
          id="picture"
          className="hidden"
          accept="image/*"
        />
        <button ref={uploadPicRef} type="submit" value="picture" className="hidden">
          Télécharger une image
        </button>
      </form>
      <form
        onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          setLoading(true);
          fetch(`${import.meta.env.VITE_API_URL}/user/${me.plausible_id}`, {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              avatar: avatarUrl,
              firstName: e.currentTarget.firstName.value,
              lastName: e.currentTarget.lastName.value,
              profession: e.currentTarget.profession.value,
              specialite: e.currentTarget.specialite?.value,
              status_pro: e.currentTarget.status_pro.value,
              service: e.currentTarget.service?.value,
              etablissement: e.currentTarget.etablissement.value,
              hopital: e.currentTarget.hopital?.value,
              rpps: e.currentTarget.rpps?.value,
            }),
          })
            .then((response) => response.json() as Promise<UserConnexionResponse>)
            .then((response) => {
              if (response.ok) {
                setUser(response.data.user);
                onOk();
              } else if (response.error) {
                alert(response.error);
              }
            })
            .finally(() => {
              setLoading(false);
            });
        }}
        method="POST"
        className={`flex w-full flex-shrink flex-grow flex-col py-4 !text-left ${className}`}
        id={id}
      >
        <Input key={avatarUrl} name="avatar" defaultValue={avatarUrl ?? ""} type="hidden" />
        <Input
          label="Prénom"
          name="firstName"
          defaultValue={defaultValues?.firstName}
          type="text"
          autoComplete="given-name"
          required
          customValidity="Entrez votre Prénom ici"
        />
        <Input
          label="Nom"
          name="lastName"
          defaultValue={defaultValues?.lastName}
          type="text"
          autoComplete="family-name"
          required
          customValidity="Entrez votre Nom de famille ici"
        />

        <Input label="Profession" name="profession" value={profession || ""} type="text" required>
          <SelectCustom
            name="profession"
            options={professionOptions}
            isMulti={false}
            isSearchable={false}
            value={professionOptions.find((option) => option.value === profession)}
            onChange={(newValue) => setProfession(newValue?.value || null)}
            instanceId="profession"
            classNamePrefix="profession"
          />
        </Input>
        {profession === Profession.MEDECIN && (
          <Input
            label="Spécialité"
            name="specialite"
            defaultValue={defaultValues?.specialite}
            type="text"
            placeholder="Anesthésie, Cardiologie, Chirurgie, Gynécologie, Neurologie, Pédiatrie, Psychiatrie, Radiologie, Urgences, etc."
            required
          >
            <SelectCustom
              name="specialite"
              isSearchable={false}
              defaultValue={
                defaultValues?.specialite
                  ? {
                      value: defaultValues.specialite,
                      label: defaultValues.specialite,
                    }
                  : undefined
              }
              options={userSpecialites.map(({ specialite }) => ({
                value: specialite,
                label: specialite,
              }))}
              isMulti={false}
              instanceId="specialite"
              classNamePrefix="specialite"
            />
          </Input>
        )}
        {!!profession && (
          <Input
            label="Statut"
            name="status_pro"
            defaultValue={defaultValues?.status_pro}
            type="text"
            required
            placeholder={profession === Profession.MEDECIN ? "PUPH, PH, CCA, AHU, Interne..." : "IDE, IADE, IBODE..."}
          >
            <SelectCustom
              name="status_pro"
              isSearchable={false}
              defaultValue={
                defaultValues?.status_pro
                  ? {
                      value: defaultValues.status_pro,
                      label: defaultValues.status_pro,
                    }
                  : undefined
              }
              options={statusOptions}
              isMulti={false}
              instanceId="status_pro"
              classNamePrefix="status_pro"
            />
          </Input>
        )}
        <SelectEtablissement etablissements={etablissements} defaultValues={defaultValues} />

        <Input
          label="Service actuel"
          name="service"
          defaultValue={defaultValues?.service}
          type="text"
          // required
          placeholder="Anesth-ortho, Soin intensif cardio, Chir-ortho-A, etc."
        />
        {profession === Profession.MEDECIN && (
          <Input
            label="Numéro RPPS (optionnel)"
            name="rpps"
            defaultValue={defaultValues?.rpps}
            // required
            type="text"
            placeholder="1010166540"
          />
        )}
        {canDelete && (
          <button
            type="button"
            onClick={handleDeleteAccount}
            className="mt-4 border-0 bg-transparent font-bold text-red-600"
          >
            Supprimer mon compte
          </button>
        )}
      </form>
    </>
  );
}
